import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import {
  OtpChallengeMetadata,
  OtpChallengeResult,
  OtpCreateChallengeRequest,
  OtpVerifyChallengeResult,
} from '@app/models/otp.model';
import { CommonUtilityService } from '@app/services/common-utility.service';
import { OtpService } from '@app/services/otp.service';
import { LinkSpacebarActivationDirective } from '@app/shared/directives/link-spacebar-activation/link-spacebar-activation.directive';
import { maskEmailAddress } from '@app/shared/utils/email.util';
import { extractHttpStatus } from '@app/shared/utils/http-error.util';
import {
  OTP_CHALLENGE_PURPOSE,
  OTP_DELIVERY_CHANNEL,
  TIME_FORMAT,
  TIME_INTERVAL,
} from '@config/app-default.constant';
import { missingPageLabels } from '@config/ush-nv-missing-page-labels';
import { DateTime } from 'luxon';
import { SessionStorageService } from '@upr-web-primer/storage';
import type { GdsSnackBarData } from '@upr-gds-web/components';
import { IdentityVerificationStep } from '../identity-verification.component';
import { ApplicationDataService } from '@app/services/application-data.service';
import { UdxSpinnerService } from '@upr-web-primer/udx-spinner';
import { WINDOW } from '@upr-web-primer/window';
import { firstValueFrom } from 'rxjs';
import { environment } from '@env/environment';
import { CookieService } from 'ngx-cookie-service';
import { isEmpty } from '@upr-web-primer/udx-utils';
import { NotificationService } from '@upr-web-primer/notification';

interface timerDetails {
  timer?: number;
  resend_timer?: number;
}

@Component({
  selector: 'app-verify-your-identity-step-2',
  templateUrl: './verify-your-identity-step-2.component.html',
  styleUrl: './verify-your-identity-step-2.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [LinkSpacebarActivationDirective],
})
export class VerifyYourIdentityStep2Component implements OnInit, OnDestroy {
  readonly challenge = input<OtpChallengeResult | null>(null);
  readonly currentStep = signal<IdentityVerificationStep>('step-2');
  readonly lastStep = input<IdentityVerificationStep>('limit-reached');

  readonly submitted = output<void>();
  readonly limitReached = output<void>();
  readonly challengeReplaced = output<OtpChallengeResult>();
  readonly verifyFailed = output<unknown>();

  /** Reference to the <gds-otp-input> custom element so we can call its
   *  public `clear()` method imperatively after verify attempts.
   *  Writable signal allows test stubs; populated by the template #otpInput ref via viewChild. */
  readonly otpInputRef =
    viewChild<ElementRef<HTMLElement & { clear(): Promise<void> }>>('otpInput');

  /** Convenience accessor for clearing the OTP input; tests can override otpInputRefOverride.
   *  Set to `null` to simulate a missing ref (bypasses the viewChild fallback). */
  otpInputRefOverride?: ElementRef<
    HTMLElement & { clear(): Promise<void> }
  > | null;

  private getOtpInputRef():
    ElementRef<HTMLElement & { clear(): Promise<void> }> | undefined {
    if (this.otpInputRefOverride !== undefined) {
      return this.otpInputRefOverride ?? undefined;
    }
    return this.otpInputRef();
  }

  emailAddress: string;
  maskedEmailAddress: string;
  codetimer: number;
  timer_details: timerDetails;
  time_interval = TIME_INTERVAL;
  readonly isResending = signal(false);
  pageLabels: Record<string, string> = {
    ...missingPageLabels,
  };
  readonly submitCTADisabled = signal(true);

  isVerifying = false;
  otpCode = '';
  otpInlineError = '';
  targetUrl: string = '';
  isWtsCartCreateAccountValue: boolean = false;
  identityVerificationUrl: string = '';
  signUpCompleteUrl = '';
  pageHeading: string = '';
  /** SR-only aria-live text; set only right after autofill/paste. */
  otpAutofillAnnouncement = '';
  resendCooldownSnackBarVisible = false;
  resendCooldownSnackBarData: GdsSnackBarData | undefined;
  private readonly resendCooldownAnnouncementDelayMs = 500;

  /** Previous raw otp length, used to detect an autofill/paste jump. */
  private previousOtpRawLength = 0;
  /** Tracks `resends_remaining` from the last successful resend POST response.
   *  Used to differentiate 429 on GET: 0 = max resends reached, else cooldown. */
  private lastKnownResendsRemaining: number | undefined;
  /** Auto-submits a complete code (typed or autofilled); exposed for tests to disable. */
  autoSubmitOnComplete = true;
  /** Delay before auto-submit, so the spinner's role="alert" doesn't pre-empt the announcement (UPT-224513). 1500ms is unconfirmed at this exact value - re-test on device. */
  autoSubmitAnnounceDelayMs = 1500;
  /** Stores the backend's expiresAt timestamp for timer resync on tab focus. */
  private expiresAtTimestamp: string | undefined;
  /** Visibility change listener bound function reference for cleanup. */
  private visibilityChangeHandler: (() => void) | undefined;
  /** Signal to control timer visibility for forcing recreation on resync. */
  readonly showTimer = signal(true);

  ngOnInit(): void {
    this.common.scrollTop();
    this.pageHeading =
      this.pageLabels?.['com.udx.account.pageinfo.steptwo'] ||
      this.globalDefault.OTP_STEP2_PAGE_NAME;
    this.emailAddress =
      this.challenge()?.destination ||
      this.sessionStorage.getItem('EMAIL_USER_ID');
    this.maskedEmailAddress = maskEmailAddress(this.emailAddress);
    this.timer_details = this.common.getTimer();
    this.codetimer = this.timer_details?.timer ?? 0;

    // Store expiresAt timestamp if available for timer resync
    this.expiresAtTimestamp = (this.timer_details as any)?.expiresAt;

    this.identityVerificationUrl = this.common.getTargetUrl(
      this.appRoutes.SIGNUP_IDENTITY_VERIFICATION,
    );
    this.signUpCompleteUrl = this.common.getTargetUrl(
      this.appRoutes.SIGNUP_COMPLETE,
    );
    this.common.triggerDDLEventHandler(this.pageHeading, {
      isPageLoad: true,
    });
    this.common.setPageName(this.pageHeading);
    this.targetUrl = this.otpService?.getNavigationUrls();

    // Setup visibility change listener to resync timer when tab regains focus
    this.setupVisibilityChangeListener();
  }
  ngOnDestroy(): void {
    // Clean up visibility change listener
    if (this.visibilityChangeHandler) {
      document.removeEventListener(
        'visibilitychange',
        this.visibilityChangeHandler,
      );
    }
  }
  /**
   * Sets up a listener for tab visibility changes to resync the timer
   * when the user returns to the tab after switching away.
   */
  private setupVisibilityChangeListener(): void {
    // Remove any existing listener before adding a new one to prevent accumulation
    // when ngOnInit is called multiple times (e.g., component reuse/re-entry)
    if (this.visibilityChangeHandler) {
      document.removeEventListener(
        'visibilitychange',
        this.visibilityChangeHandler,
      );
    }

    this.visibilityChangeHandler = (): void => {
      if (!document.hidden && this.expiresAtTimestamp) {
        this.resyncTimerFromBackend();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }
  /**
   * Recalculates the timer based on the backend's expiresAt timestamp
   * to fix drift caused by browser throttling when tab is not in focus.
   */
  private resyncTimerFromBackend(): void {
    if (!this.expiresAtTimestamp) {
      return;
    }
    const currentTimeStamp = DateTime.now().toUTC().toISO();
    let newTimer = 0;
    if (this.expiresAtTimestamp) {
      newTimer = Math.trunc(
        this.getTimeDifference(currentTimeStamp, this.expiresAtTimestamp),
      );
    }

    // Only update if the timer is positive (not expired) and different from current
    if (newTimer > 0 && newTimer !== this.codetimer) {
      // Temporarily hide timer to force component destruction
      this.showTimer.set(false);

      // Update the timer value
      this.codetimer = newTimer;

      // Show timer again in next tick to force recreation with new duration
      setTimeout(() => {
        this.showTimer.set(true);
      }, 0);
    }
  }
  readonly sessionStorage = inject(SessionStorageService);
  readonly common = inject(CommonUtilityService);
  private readonly otpService = inject(OtpService);
  private readonly applicationDataService = inject(ApplicationDataService);
  readonly spinner = inject(UdxSpinnerService);
  readonly window = inject<any>(WINDOW);
  readonly cookieService = inject(CookieService);
  private readonly appRoutes = inject<any>('AppRoutes' as any);
  readonly globalDefault = inject<any>('GlobalDefaults' as any);
  private readonly notification = inject(NotificationService);
  private readonly liveAnnouncer = inject(LiveAnnouncer);
  readonly triggerDDLMappings = inject<any>('TriggerDDLMappings' as any);

  constructor() {
    this.pageLabels = {
      ...this.pageLabels,
      ...this.common.getResolvedPageData(),
    };
  }

  showLimitReached(): void {
    this.submitCTADisabled.set(true);
    this.currentStep.set(this.lastStep());
    this.limitReached.emit();
  }

  /* Clear the inline error only once the guest has entered a full 6-digit
    code. Clearing on partial input would wipe the expired-code message
    before the guest can read/act on it, and clearing on empty input would
    erase the message we just set right before gds-otp-input.clear() emits
    valueChange('') as a side effect of the imperative clear in the 404 branch. */
  onValueChange(event: string): void {
    const isOtpComplete = /^\d{6}$/.test(event);

    // A >1 digit jump can't come from typing - it's autofill or paste.
    const isAutofillOrPaste =
      isOtpComplete && event.length - this.previousOtpRawLength > 1;
    this.previousOtpRawLength = event.length;

    if (this.otpInlineError && isOtpComplete) {
      this.otpInlineError = '';
    }
    if (isOtpComplete) {
      this.submitCTADisabled.set(false);
    } else {
      this.submitCTADisabled.set(true);
    }
    // this.submitCTADisabled = isOtpComplete ? 'false' : 'true';
    this.otpCode = isOtpComplete ? event : '';

    // Auto-submit once complete, typed or autofilled (UPT-222233).
    const willAutoSubmit =
      this.autoSubmitOnComplete &&
      isOtpComplete &&
      !this.isVerifying &&
      !!this.challenge()?.challengeId;

    // One announcement for autofill/paste only - manual typers already know they entered it (UPT-224513).
    this.otpAutofillAnnouncement =
      isAutofillOrPaste && willAutoSubmit
        ? this.pageLabels?.[
            'com.udx.account.onetimepasscode.autofill_announcement'
          ] || 'Code entered. Verifying.'
        : '';

    if (willAutoSubmit) {
      const runAutoSubmit = (): void => {
        // Guest may have deleted a digit before this fires.
        if (this.otpCode && !this.isVerifying) {
          void this.verifyCode();
        }
      };

      // Head start before spinner.show()'s role="alert" pre-empts the announcement (UPT-224513).
      if (isAutofillOrPaste && this.autoSubmitAnnounceDelayMs > 0) {
        setTimeout(runAutoSubmit, this.autoSubmitAnnounceDelayMs);
      } else {
        queueMicrotask(runAutoSubmit);
      }
    }
  }

  /** Submit the OTP; emits `submitted` on success or `verifyFailed`/`limitReached` on error. */
  async verifyCode(): Promise<void> {
    const challengeId = this.challenge()?.challengeId;

    // Validation guard - must check BEFORE showing spinner to prevent indefinite loading state
    if (!challengeId || !this.otpCode || this.isVerifying) {
      return;
    }

    // Flip guard state synchronously to close the double-click race window.
    this.submitCTADisabled.set(true);
    this.isVerifying = true;
    this.spinner.show();

    try {
      const result: OtpVerifyChallengeResult =
        await this.otpService.verifyChallenge(challengeId, {
          code: this.otpCode,
        });
      const emailVerifyResult = await this.emailVerification(challengeId);
      this.isWtsCartCreateAccountValue = this.isWtsCartCreateAccount();
      if (
        result?.verified &&
        emailVerifyResult &&
        !this.isWtsCartCreateAccountValue
      ) {
        this.submitted.emit();
        this.redirectToDtfReturnUrl();
        this.common.triggerDDLEventHandler(
          this.pageLabels?.['com.udx.account.onetimepasscode.button_submit'],
          {
            isButtonClick: true,
            eventTargetUrl: this.signUpCompleteUrl,
          },
        );
        this.clearUserSession();
      } else if (
        result?.verified &&
        emailVerifyResult &&
        this.isWtsCartCreateAccountValue
      ) {
        const hotelNavigation = this.cookieService.get('Hotel');
        const isHotelNavigation = !isEmpty(hotelNavigation);
        const navigationURL = isHotelNavigation
          ? environment.htlHostBaseUrl
          : environment.wtsHostBaseUrl;
        this.targetUrl = navigationURL;
        this.common.triggerDDLEventHandler(
          this.pageLabels?.['com.udx.account.onetimepasscode.button_submit'],
          {
            isButtonClick: true,
            eventTargetUrl: navigationURL,
          },
        );
        this.otpService.commerceAuthentication();
      } else {
        this.common.triggerDDLEventHandler(
          this.pageLabels?.['com.udx.account.onetimepasscode.button_submit'],
          {
            isButtonClick: true,
            eventTargetUrl: this.identityVerificationUrl,
          },
        );
        await this.getOtpInputRef()?.nativeElement.clear();
        this.verifyFailed.emit(result);
      }
    } catch (error: unknown) {
      const status = extractHttpStatus(error);

      // 429 → attempts exhausted. Navigate to the dedicated limit-reached page; OtpService suppresses the banner for 429 on verifyChallenge so the page navigation is the only UX signal.
      if (status === 429) {
        this.otpInlineError =
          this.pageLabels?.[
            'com.udx.account.onetimepasscode.verification_limit_reached_message'
          ];
        this.common.triggerDDLEventHandler(
          this.triggerDDLMappings.INLINE_ERROR,
          {
            isError: true,
            eventTargetUrl: this.window.location.href,
            errorMessage: this.otpInlineError,
          },
        );
        this.limitReached.emit();
        return;
      }
      // 404 → expired code, show inline error instead of a banner.
      if (status === 404) {
        this.otpInlineError =
          this.pageLabels?.['com.udx.account.onetimepasscode.error_expired'] ||
          'Your verification code has expired. Please request a new code.';
        await this.getOtpInputRef()?.nativeElement.clear();
        this.common.triggerDDLEventHandler(
          this.triggerDDLMappings.INLINE_ERROR,
          {
            isError: true,
            eventTargetUrl: this.window.location.href,
            errorMessage: this.otpInlineError,
          },
        );

        this.verifyFailed.emit(error);
        return;
      }

      // 422 → invalid code (wrong digits). Show inline error under the OTP input; the guest stays on step-2 so they can retry without navigating.
      if (status === 422) {
        this.otpInlineError =
          this.pageLabels?.[
            'com.udx.account.onetimepasscode.error_incorrect'
          ] || 'The code you entered is incorrect. Please try again.';
        await this.getOtpInputRef()?.nativeElement.clear();
        this.common.triggerDDLEventHandler(
          this.triggerDDLMappings.INLINE_ERROR,
          {
            isError: true,
            eventTargetUrl: this.window.location.href,
            errorMessage: this.otpInlineError,
          },
        );
        this.verifyFailed.emit(error);
        return;
      }

      await this.getOtpInputRef()?.nativeElement.clear();
      this.verifyFailed.emit(error);
    } finally {
      this.spinner.hide();
      this.isVerifying = false;
    }
  }

  private redirectToDtfReturnUrl(): void {
    const dtfReturnUrl = this.sessionStorage.getItem('dtfReturnUrl');

    if (!dtfReturnUrl) {
      return;
    }

    const allowedHostnames = [
      'universalorlando.com',
      'universalstudioshollywood.com',
      'universalhorrorunleashed.com',
      'universalkidsresort.com',
    ];

    try {
      const url = new URL(dtfReturnUrl);
      this.targetUrl = dtfReturnUrl;
      const isAllowedHost = allowedHostnames.some(
        (hostname) =>
          url.hostname === hostname || url.hostname.endsWith(`.${hostname}`),
      );

      if (url.protocol !== 'https:' || !isAllowedHost) {
        return;
      }

      this.window.location.href = url.href;
    } catch {
      return;
    }
  }

  async onResendCode(): Promise<void> {
    if (this.isResending()) {
      return;
    }

    const currentChallengeId = this.challenge()?.challengeId;
    if (!currentChallengeId) {
      return;
    }

    this.isResending.set(true);
    this.spinner.show();
    this.otpInlineError = '';
    this.targetUrl = this.window.location.href;

    // Clear any stale inline error and OTP input immediately on click.
    // The entered code belongs to the previous challenge.
    const clearOtpInput = this.getOtpInputRef()?.nativeElement.clear;
    if (typeof clearOtpInput === 'function') {
      await clearOtpInput.call(this.getOtpInputRef()?.nativeElement);
    }

    try {
      // ── Step A: POST /resend ──
      const newChallenge =
        await this.otpService.resendChallenge(currentChallengeId);

      // Capture resends_remaining from the resend response
      this.lastKnownResendsRemaining = newChallenge.resendsRemaining;

      // ── Step B: GET /otp-challenges/{id} ──
      let challengeMetadata: OtpChallengeMetadata;
      try {
        challengeMetadata = await this.otpService.getChallenge(
          newChallenge.challengeId,
        );
      } catch (getError: unknown) {
        const status = extractHttpStatus(getError);

        // 429 on GET after successful resend
        if (status === 429) {
          this.handleResend429();
          return;
        }
        // Non-429 GET errors (401/404/500/502/503): banner was already shown
        // by OtpService.handleGetChallengeHttpError. Return here so a GET
        // failure after a successful resend doesn't fall into the outer catch
        // and surface a second banner (UPT-229829, UPT-230641).
        return;
      }

      // ── Step C: Success path – update timers ──
      if (
        !challengeMetadata.expiresAt ||
        !DateTime.fromISO(challengeMetadata.expiresAt).isValid ||
        !challengeMetadata.resendAvailableAt ||
        !DateTime.fromISO(challengeMetadata.resendAvailableAt).isValid
      ) {
        return;
      }

      // Also update resendsRemaining from GET response if available
      if (challengeMetadata.resendsRemaining !== undefined) {
        this.lastKnownResendsRemaining = challengeMetadata.resendsRemaining;
      }

      const currentTimeStamp = DateTime.now().toUTC().toISO();

      const timer = Math.trunc(
        this.getTimeDifference(currentTimeStamp, challengeMetadata.expiresAt),
      );
      const resend_timer = Math.trunc(
        this.getTimeDifference(
          currentTimeStamp,
          challengeMetadata.resendAvailableAt,
        ),
      );

      this.common.setTimer({
        timer,
        resend_timer,
        expiresAt: challengeMetadata?.expiresAt,
      });

      this.expiresAtTimestamp = challengeMetadata.expiresAt;
      this.showTimer.set(false);

      this.codetimer = timer;
      // Show timer again in next tick to force recreation with new duration
      setTimeout(() => {
        this.showTimer.set(true);
      }, 0);
      this.hideResendCooldownSnackBar();
      this.challengeReplaced.emit(newChallenge);
    } catch (error: any) {
      const status = extractHttpStatus(error);

      if (status === 429) {
        this.handleResend429(error);
        return;
      }

      if (status === 409) {
        this.otpInlineError =
          this.pageLabels?.[
            'com.udx.account.onetimepasscode.error_incorrect'
          ] || 'The code you entered is incorrect. Please try again.';

        this.common.triggerDDLEventHandler(
          this.triggerDDLMappings.INLINE_ERROR,
          {
            isError: true,
            eventTargetUrl: this.targetUrl,
            errorMessage: this.otpInlineError,
          },
        );
        return;
      }

      // An expired challenge cannot be resent. Start a replacement challenge
      // and refresh its metadata so the OTP input and timer can be reused.
      if (status === 404) {
        await this.createChallenge();
        return;
      }

      // Other POST /resend failures (500, 503):
      // Banner is already shown by OtpService.handleResendHttpError.
      // OTP input was already cleared above. Do not set otpInlineError because
      // the existing challenge is still valid and the timer continues counting down.
    } finally {
      this.spinner.hide();
      this.isResending.set(false);
    }
  }

  private handleResend429(error?: any): void {
    if (this.lastKnownResendsRemaining === 0) {
      if (error) {
        this.common.triggerSystemErrorDDLEvent(error.error?.detail);
      }
      this.showLimitReached();
      return;
    }

    this.showResendCooldownSnackBar();
  }
  hideResendCooldownSnackBar(): void {
    this.resendCooldownSnackBarVisible = false;
    this.liveAnnouncer.clear();
  }

  private showResendCooldownSnackBar(): void {
    const cooldownMessage =
      this.pageLabels?.['com.udx.account.onetimepasscode.resend_cooldown'] ||
      "A new code wasn't sent. Please wait before requesting another code.";

    this.resendCooldownSnackBarData = {
      label: cooldownMessage,
      ariaLabel: 'Close',
      feature: {
        icon: 'caution',
        iconTitle: 'Caution',
        description: cooldownMessage,
        svg: {
          fillColor: '#EC1018',
          strokeColor: '#EC1018',
        },
      },
    };
    this.resendCooldownSnackBarVisible = true;

    this.common.triggerDDLEventHandler(this.triggerDDLMappings.INLINE_ERROR, {
      isError: true,
      eventTargetUrl: this.window.location.href,
      errorMessage: cooldownMessage,
    });

    // Announce after the resend error flow has returned and the spinner has
    // been hidden. VoiceOver was ignoring the first failed resend when the
    // announcement fired too close to the spinner/snackbar DOM updates.
    this.liveAnnouncer.clear();
    setTimeout(() => {
      this.liveAnnouncer.announce(cooldownMessage, 'polite');
    }, this.resendCooldownAnnouncementDelayMs);
  }

  private getTimeDifference(
    startTime: string | null,
    endTime: string | undefined,
  ): number {
    if (!endTime) {
      return 0;
    }

    const expirationTime = DateTime.fromISO(endTime);
    const difference = expirationTime.diff(
      DateTime.fromISO(startTime!),
      TIME_FORMAT,
    );

    return difference.as('seconds');
  }

  private isWtsCartCreateAccount(): boolean {
    return (
      this.applicationDataService.getCookieInfo('cartCreateAccount') === 'true'
    );
  }
  private clearUserSession(): void {
    this.sessionStorage.removeItem('EMAIL_USER_ID');
    this.sessionStorage.removeItem('LOGGEDIN_GUEST_ID');
  }

  async emailVerification(challengeId: string): Promise<boolean> {
    const guestId = localStorage.getItem('LOGGEDIN_GUEST_ID') || '';
    const requestPayload = this.common.getEmailVerificationRequestPayload(
      true,
      challengeId,
    );

    try {
      await firstValueFrom(
        this.common.verifyEmailAddress(guestId, requestPayload),
      );
      return true;
    } catch (error) {
      this.otpInlineError =
        this.pageLabels?.['errCode.E019'] ||
        'There was a problem. Please try again later';
      this.notification.show(this.otpInlineError);
      const errorData: {
        error: unknown;
        isEmailVerify: boolean;
      } = {
        error,
        isEmailVerify: true,
      };
      this.verifyFailed.emit(errorData);
      return false;
    }
  }

  async createChallenge(): Promise<void> {
    this.spinner.show();
    try {
      const request = this.buildCreateChallengeRequest();
      const challenge = await this.otpService.createChallenge(request);

      const challenge_response = await this.otpService.getChallenge(
        challenge.challengeId,
      );

      const currentTimeStamp = DateTime.now().toUTC().toISO();

      const getTimer = this.getTimeDifference(
        currentTimeStamp,
        challenge_response.expiresAt,
      );
      const next_resend_available = this.getTimeDifference(
        currentTimeStamp,
        challenge_response.resendAvailableAt,
      );
      const time = Math.trunc(getTimer);
      const next_resend_available_at = Math.trunc(next_resend_available);
      this.challengeReplaced.emit(challenge);
      const code_timer = {
        timer: time,
        challengeId: challenge.challengeId,
        resend_timer: next_resend_available_at,
        expiresAt: challenge_response.expiresAt,
      };

      this.expiresAtTimestamp = challenge_response.expiresAt;
      this.showTimer.set(false);

      this.codetimer = time;
      // Show timer again in next tick to force recreation with new duration
      setTimeout(() => {
        this.showTimer.set(true);
      }, 0);
      this.common.setTimer(code_timer);
    } catch (error: unknown) {
      if (error)
        this.otpInlineError =
          this.pageLabels?.['errCode.E019'] ||
          'There was a problem. Please try again later';
      this.notification.show(this.otpInlineError);
    } finally {
      this.spinner.hide();
    }
  }

  private buildCreateChallengeRequest(): OtpCreateChallengeRequest {
    const guestId = localStorage.getItem('LOGGEDIN_GUEST_ID') || '';

    return {
      destination:
        this.challenge()?.destination ||
        this.sessionStorage.getItem('EMAIL_USER_ID') ||
        null,
      purpose: OTP_CHALLENGE_PURPOSE.EMAIL_VERIFICATION,
      channel: OTP_DELIVERY_CHANNEL.EMAIL,
      ...(guestId && { guest_id: guestId }),
    };
  }

  onSpacebarPress(event: KeyboardEvent): void {
    if (event.code === 'Space') {
      event.preventDefault();
      this.onResendCode();
    }
  }
}
